import { Users, Plus, Trash2, Loader2, Eye, EyeOff, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { useEffect, useState, useRef, useMemo } from "react";
import FormInput from "@/app/_module/components/FormInput";
import { Button } from "@/app/_module/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { FormField } from "@/app/_module/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { useBeneficiaries, useDeleteBeneficiary } from "../hooks/queries";
import { useGetLeadHouseholds } from "../hooks/households";

interface BeneficiarySectionProps {
  isEditing: boolean;
  leadId?: string;
}

// Common relationship options
const RELATIONSHIP_OPTIONS = [
  "Spouse",
  "Child",
  "Parent",
  "Sibling",
  "Grandchild",
  "Grandparent",
  "Partner",
  "Friend",
  "Other",
] as const;

// Helper function to unwrap household API responses
function unwrapApiResponse(response: any): any[] {
  if (!response) return [];

  if (response?.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }

  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

export const BeneficiarySection = function ({ isEditing, leadId }: BeneficiarySectionProps) {
  const { control, formState: { errors }, watch, setValue, getValues } = useFormContext();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "beneficiaries",
  });

  // Fetch beneficiaries from backend
  const { data: beneficiaries, isLoading, error, refetch } = useBeneficiaries(leadId);
  const deleteBeneficiaryMutation = useDeleteBeneficiary();

  // Fetch household members for dropdown
  const { data: householdsResponse } = useGetLeadHouseholds(leadId || "", !!leadId);
  const householdMembers = unwrapApiResponse(householdsResponse);

  // Track dropdown visibility for each beneficiary
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Track SSN visibility for each beneficiary
  const [visibleSSNs, setVisibleSSNs] = useState<Record<number, boolean>>({});

  // Track beneficiary pending deletion for confirmation dialog
  const [deletingBeneficiaryIndex, setDeletingBeneficiaryIndex] = useState<number | null>(null);

  // Track which beneficiaries have contingent section expanded
  const [expandedContingent, setExpandedContingent] = useState<Record<number, boolean>>({});

  // Track if we've initialized the form to prevent overwriting user edits
  const hasInitialized = useRef(false);
  const previousBeneficiariesRef = useRef<string>('');

  // Force refetch on mount to bypass cache
  useEffect(() => {
    if (leadId) {
      refetch();
    }
  }, [leadId, refetch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeDropdown]);

  // Sync fetched beneficiaries with form state
  useEffect(() => {
    if (beneficiaries && !isLoading) {
      // Create a fingerprint of the beneficiaries to detect changes
      const currentFingerprint = JSON.stringify(beneficiaries.map(b => ({
        id: b.id,
        name: b.name,
        relationship: b.relationship,
        percentage: b.percentage,
        dateOfBirth: b.dateOfBirth,
        ssn: b.ssn,
        contingentName: b.contingentName,
        contingentRelationship: b.contingentRelationship,
        contingentPercentage: b.contingentPercentage,
      })));

      // Only update if data has actually changed
      if (currentFingerprint !== previousBeneficiariesRef.current) {
        if (beneficiaries.length > 0) {
          // Map backend data to form format
          const formattedBeneficiaries = beneficiaries.map((b) => {
            return {
              id: b.id, // Include ID for updates
              name: b.name,
              relationship: b.relationship || "",
              percentage: b.percentage || 0,
              dob: b.dateOfBirth || "",
              ssn: b.ssn || "",
              contingentName: b.contingentName || "",
              contingentRelationship: b.contingentRelationship || "",
              contingentPercentage: b.contingentPercentage || 0,
            };
          });
          replace(formattedBeneficiaries);
        } else {
          replace([]);
        }

        previousBeneficiariesRef.current = currentFingerprint;
        hasInitialized.current = true;
      }
    }
  }, [beneficiaries, isLoading, replace]);

  // Watch all beneficiaries to calculate total percentage
  const beneficiariesData = watch("beneficiaries") || [];
  const totalPercentage = useMemo(
    () => beneficiariesData.reduce(
      (sum: number, b: any) => sum + (Number(b.percentage) || 0),
      0
    ),
    [beneficiariesData]
  );

  const addBeneficiary = () => {
    append({ name: "", relationship: "", percentage: 0, dob: "", ssn: "", isHouseholdMember: false, contingentName: "", contingentRelationship: "", contingentPercentage: 0 });
  };

  const toggleContingent = (index: number) => {
    setExpandedContingent(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleDeleteClick = (index: number) => {
    setDeletingBeneficiaryIndex(index);
  };

  const handleConfirmDelete = async () => {
    if (deletingBeneficiaryIndex === null) return;

    const beneficiary = beneficiariesData[deletingBeneficiaryIndex];

    // If beneficiary has an ID, it exists in the database - delete it
    if (beneficiary?.id && leadId) {
      try {
        await deleteBeneficiaryMutation.mutateAsync({
          beneficiaryId: beneficiary.id,
          leadId: leadId,
        });
        // Remove from form after successful deletion
        remove(deletingBeneficiaryIndex);
      } catch (error) {
        // Error handled by mutation
      }
    } else {
      // Just remove from form if not yet saved
      remove(deletingBeneficiaryIndex);
    }

    setDeletingBeneficiaryIndex(null);
  };

  const handleCancelDelete = () => {
    setDeletingBeneficiaryIndex(null);
  };

  if (isLoading && leadId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">Beneficiaries</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-gray-600">Loading beneficiaries...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">Beneficiaries</h3>
        </div>
        <div className="text-center py-6 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-600">Failed to load beneficiaries</p>
          {isEditing && (
            <Button
              className="mt-3"
              onClick={addBeneficiary}
              size="sm"
              type="button"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Beneficiary
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-gray-900">Beneficiaries</h3>
      </div>

      {/* Beneficiaries List */}
      {fields.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No beneficiaries added</p>
          {isEditing && (
            <Button
              className="mt-3"
              onClick={addBeneficiary}
              size="sm"
              type="button"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Beneficiary
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="border rounded-lg p-4 space-y-3 bg-gray-50"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">
                  Beneficiary {index + 1}
                </h4>
                {isEditing && (
                  <Button
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteClick(index)}
                    size="sm"
                    type="button"
                    variant="ghost"
                    disabled={deleteBeneficiaryMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Name Field with Household Members Dropdown */}
                <div className="md:col-span-2 relative">
                  <FormField
                    control={control}
                    name={`beneficiaries.${index}.name`}
                    render={({ field: inputField }) => (
                      <div className="space-y-2">
                        <label
                          className="text-sm font-medium text-gray-700"
                          htmlFor={`beneficiary-name-${index}`}
                        >
                          Full Name
                        </label>
                        <div className="relative" ref={activeDropdown === index ? dropdownRef : null}>
                          <input
                            {...inputField}
                            disabled={!isEditing}
                            id={`beneficiary-name-${index}`}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              (errors.beneficiaries as any)?.[index]?.name
                                ? "border-red-500"
                                : "border-gray-300"
                            } ${!isEditing ? "bg-gray-100" : "bg-white"}`}
                            placeholder="Enter beneficiary's full name or select from household"
                            onFocus={() => isEditing && setActiveDropdown(index)}
                            onChange={(e) => {
                              inputField.onChange(e);
                              if (!isEditing) setActiveDropdown(null);
                            }}
                          />
                          {/* Dropdown with household members */}
                          {isEditing && activeDropdown === index && householdMembers.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 shadow-lg rounded-md max-h-48 overflow-y-auto">
                              {householdMembers.map((member: any) => (
                                <button
                                  key={member.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-100 cursor-pointer border-b last:border-b-0"
                                  onClick={() => {
                                    inputField.onChange(member.name || '');

                                    // Mark as household member to hide DOB field
                                    setValue(`beneficiaries.${index}.isHouseholdMember`, true, { shouldDirty: true });

                                    // Auto-populate fields from household member if available
                                    if (member.dob) {
                                      setValue(`beneficiaries.${index}.dob`, member.dob, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                    }

                                    if (member.relationship) {
                                      setValue(`beneficiaries.${index}.relationship`, member.relationship, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                    }

                                    if (member.percentage) {
                                      const percentageNum = parseFloat(member.percentage);
                                      setValue(`beneficiaries.${index}.percentage`, percentageNum, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                    }

                                    setActiveDropdown(null);
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-900">
                                      {member.name || 'Unknown'}
                                    </span>
                                    {member.isPrimary && (
                                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                        Primary
                                      </span>
                                    )}
                                  </div>
                                  {member.relationship && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {member.relationship}
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {(errors.beneficiaries as any)?.[index]?.name && (
                          <p className="text-sm text-red-500">
                            {(errors.beneficiaries as any)?.[index]?.name?.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Relationship Field */}
                <div>
                  <FormField
                    control={control}
                    name={`beneficiaries.${index}.relationship`}
                    render={({ field: inputField }) => (
                      <div className="space-y-2">
                        <label
                          className="text-sm font-medium text-gray-700"
                          htmlFor={`beneficiary-relationship-${index}`}
                        >
                          Relationship
                        </label>
                        {isEditing ? (
                          <Select
                            onValueChange={inputField.onChange}
                            value={inputField.value || ""}
                          >
                            <SelectTrigger
                              className={
                                (errors.beneficiaries as any)?.[index]?.relationship
                                  ? "border-red-500"
                                  : ""
                              }
                              id={`beneficiary-relationship-${index}`}
                            >
                              <SelectValue placeholder="Select relationship" />
                            </SelectTrigger>
                            <SelectContent>
                              {RELATIONSHIP_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <input
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                            disabled
                            value={inputField.value || "-"}
                          />
                        )}
                        {(errors.beneficiaries as any)?.[index]?.relationship && (
                          <p className="text-sm text-red-500">
                            {(errors.beneficiaries as any)?.[index]?.relationship?.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Percentage Field */}
                <div>
                  <FormField
                    control={control}
                    name={`beneficiaries.${index}.percentage`}
                    render={({ field: inputField }) => (
                      <FormInput
                        {...inputField}
                        disabled={!isEditing}
                        error={(errors.beneficiaries as any)?.[index]?.percentage}
                        id={`beneficiary-percentage-${index}`}
                        label="Percentage"
                        max="100"
                        min="0"
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty string or whole numbers only (0-100)
                          if (value === "") {
                            inputField.onChange("");
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                              inputField.onChange(numValue);
                            }
                          }
                        }}
                        placeholder="0"
                        step="1"
                        type="number"
                      />
                    )}
                  />
                </div>

                {/* Date of Birth Field (Optional) - Hidden for household members */}
                {!beneficiariesData[index]?.isHouseholdMember && (
                  <div>
                    <FormField
                      control={control}
                      name={`beneficiaries.${index}.dob`}
                      render={({ field: inputField }) => (
                        <FormInput
                          {...inputField}
                          disabled={!isEditing}
                          error={(errors.beneficiaries as any)?.[index]?.dob}
                          id={`beneficiary-dob-${index}`}
                          label="Date of Birth (Optional)"
                          type="date"
                        />
                      )}
                    />
                  </div>
                )}

                {/* SSN Last 4 Field (Optional) */}
                <div>
                  <FormField
                    control={control}
                    name={`beneficiaries.${index}.ssn`}
                    render={({ field: inputField }) => (
                      <div className="space-y-2">
                        <label
                          className="text-sm font-medium text-gray-700"
                          htmlFor={`beneficiary-ssn-${index}`}
                        >
                          Last 4 SSN (Optional)
                        </label>
                        <div className="relative">
                          <input
                            id={`beneficiary-ssn-${index}`}
                            disabled={!isEditing}
                            className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              (errors.beneficiaries as any)?.[index]?.ssn
                                ? "border-red-500"
                                : "border-gray-300"
                            } ${!isEditing ? "bg-gray-100" : "bg-white"}`}
                            placeholder="XXXX"
                            maxLength={4}
                            type={visibleSSNs[index] ? "text" : "password"}
                            value={inputField.value || ''}
                            onChange={(e) => {
                              // Only allow numbers, max 4 digits
                              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                              inputField.onChange(value);
                            }}
                          />
                          {/* Show/Hide Toggle Button */}
                          {inputField.value && (
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                              onClick={() => setVisibleSSNs(prev => ({ ...prev, [index]: !prev[index] }))}
                              tabIndex={-1}
                            >
                              {visibleSSNs[index] ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                        {(errors.beneficiaries as any)?.[index]?.ssn && (
                          <p className="text-sm text-red-500">
                            {(errors.beneficiaries as any)?.[index]?.ssn?.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Contingent Beneficiary Section (Optional) */}
              <div className="mt-4 border-t border-gray-200 pt-3">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 w-full"
                  onClick={() => toggleContingent(index)}
                >
                  {expandedContingent[index] ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <span className="font-medium">
                    Contingent Beneficiary (Optional)
                  </span>
                  {beneficiariesData[index]?.contingentName && !expandedContingent[index] && (
                    <span className="text-gray-500 ml-2">
                      — {beneficiariesData[index].contingentName}
                    </span>
                  )}
                </button>

                {expandedContingent[index] && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Contingent Name Field */}
                    <div>
                      <FormField
                        control={control}
                        name={`beneficiaries.${index}.contingentName`}
                        render={({ field: inputField }) => (
                          <FormInput
                            {...inputField}
                            disabled={!isEditing}
                            error={(errors.beneficiaries as any)?.[index]?.contingentName}
                            id={`beneficiary-contingent-name-${index}`}
                            label="Contingent Name"
                            placeholder="Full name"
                            type="text"
                          />
                        )}
                      />
                    </div>

                    {/* Contingent Relationship Field */}
                    <div>
                      <FormField
                        control={control}
                        name={`beneficiaries.${index}.contingentRelationship`}
                        render={({ field: inputField }) => (
                          <div className="space-y-2">
                            <label
                              className="text-sm font-medium text-gray-700"
                              htmlFor={`beneficiary-contingent-relationship-${index}`}
                            >
                              Relationship
                            </label>
                            {isEditing ? (
                              <Select
                                onValueChange={inputField.onChange}
                                value={inputField.value || ""}
                              >
                                <SelectTrigger
                                  className={
                                    (errors.beneficiaries as any)?.[index]?.contingentRelationship
                                      ? "border-red-500"
                                      : ""
                                  }
                                  id={`beneficiary-contingent-relationship-${index}`}
                                >
                                  <SelectValue placeholder="Select relationship" />
                                </SelectTrigger>
                                <SelectContent>
                                  {RELATIONSHIP_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {option}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <input
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                                disabled
                                value={inputField.value || "-"}
                              />
                            )}
                            {(errors.beneficiaries as any)?.[index]?.contingentRelationship && (
                              <p className="text-sm text-red-500">
                                {(errors.beneficiaries as any)?.[index]?.contingentRelationship?.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </div>

                    {/* Contingent Percentage Field */}
                    <div>
                      <FormField
                        control={control}
                        name={`beneficiaries.${index}.contingentPercentage`}
                        render={({ field: inputField }) => (
                          <FormInput
                            {...inputField}
                            disabled={!isEditing}
                            error={(errors.beneficiaries as any)?.[index]?.contingentPercentage}
                            id={`beneficiary-contingent-percentage-${index}`}
                            label="Percentage"
                            max="100"
                            min="0"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                inputField.onChange("");
                              } else {
                                const numValue = parseInt(value, 10);
                                if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                                  inputField.onChange(numValue);
                                }
                              }
                            }}
                            placeholder="0"
                            step="1"
                            type="number"
                          />
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Total Percentage Display */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <span className="text-sm font-medium text-gray-700">
              Total Allocation:
            </span>
            <span
              className={`text-lg font-bold ${
                Math.abs(totalPercentage - 100) < 0.01
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {totalPercentage.toFixed(2)}%
            </span>
          </div>

          {Math.abs(totalPercentage - 100) > 0.01 && (
            <p className="text-xs text-red-500 text-center">
              ⚠️ Total percentage should equal 100%
            </p>
          )}

          {/* Add Another Button (only in edit mode) */}
          {isEditing && (
            <Button
              className="text-primary hover:text-primary/80 hover:bg-primary/5 p-2 h-auto w-full"
              onClick={addBeneficiary}
              type="button"
              variant="ghost"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add another beneficiary
            </Button>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deletingBeneficiaryIndex !== null} onOpenChange={(open) => !open && handleCancelDelete()}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Beneficiary
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>
                {deletingBeneficiaryIndex !== null
                  ? beneficiariesData[deletingBeneficiaryIndex]?.name || `Beneficiary ${deletingBeneficiaryIndex + 1}`
                  : "this beneficiary"}
              </strong>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBeneficiaryMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteBeneficiaryMutation.isPending}
              onClick={handleConfirmDelete}
            >
              {deleteBeneficiaryMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>
    </div>
  );
};